import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import DashboardSidebarAdmin from '../components/Dashboard/DashboardSidebarAdmin';
import DashboardNavbarAdmin from '../components/Dashboard/DashboardNavbarAdmin';
import { Edit, Trash2, Plus, Eye, List, X } from 'lucide-react';

const AdminBlogPage = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal State
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [categoryLoading, setCategoryLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        const [blogRes, catRes] = await Promise.all([
            supabase.from('blogs').select('*, category:blog_categories(name)').order('created_at', { ascending: false }),
            supabase.from('blog_categories').select('*').order('name')
        ]);

        if (blogRes.error) console.error('Error fetching blogs:', blogRes.error);
        if (catRes.error) console.error('Error fetching categories:', catRes.error);

        setBlogs(blogRes.data || []);
        setCategories(catRes.data || []);
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Yakin ingin menghapus artikel ini?')) return;
        const { error } = await supabase.from('blogs').delete().eq('id', id);
        if (error) alert('Gagal menghapus: ' + error.message);
        else fetchData();
    };

    /* ================= CATEGORY MANAGAMENT LOGIC ================= */
    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) return;
        setCategoryLoading(true);

        const slug = newCategoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

        const { error } = await supabase.from('blog_categories').insert([{ name: newCategoryName, slug }]);

        if (error) {
            alert('Gagal menambah kategori: ' + error.message);
        } else {
            setNewCategoryName('');
            // Refresh categories only
            const { data } = await supabase.from('blog_categories').select('*').order('name');
            setCategories(data || []);
        }
        setCategoryLoading(false);
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('Hapus kategori ini? Kategori tidak bisa dihapus jika sedang digunakan oleh artikel.')) return;

        const { error } = await supabase.from('blog_categories').delete().eq('id', id);
        if (error) {
            alert('Tidak bisa menghapus kategori (mungkin sedang digunakan).');
        } else {
            setCategories(categories.filter(c => c.id !== id));
        }
    };

    return (
        <div className="h-screen flex bg-slate-50 font-sans overflow-hidden">
            <DashboardSidebarAdmin />

            <div className="flex-1 flex flex-col min-w-0">
                <DashboardNavbarAdmin displayName="Admin" />

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 relative">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Blog Management</h1>
                                <p className="text-slate-500 text-sm">Kelola artikel dan kategori blog.</p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowCategoryModal(true)}
                                    className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-50 transition"
                                >
                                    <List size={16} /> Kelola Kategori
                                </button>
                                <button
                                    onClick={() => navigate('/dashboardadmin/blog/new')}
                                    className="bg-sky-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-sky-700 transition"
                                >
                                    <Plus size={16} /> Buat Artikel
                                </button>
                            </div>
                        </div>

                        {/* BLOG TABLE */}
                        {loading ? (
                            <div className="text-center py-10">Loading...</div>
                        ) : blogs.length === 0 ? (
                            <div className="bg-white p-10 rounded-xl border border-dashed border-slate-300 text-center text-slate-500">
                                Belum ada artikel blog.
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-100 text-slate-600 font-semibold uppercase text-xs">
                                        <tr>
                                            <th className="px-6 py-4">Judul Artikel</th>
                                            <th className="px-6 py-4">Tanggal</th>
                                            <th className="px-6 py-4">Kategori</th>
                                            <th className="px-6 py-4 text-center">Views</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4 text-right">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {blogs.map(blog => (
                                            <tr key={blog.id} className="hover:bg-slate-50 transition">
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-slate-800">{blog.title}</div>
                                                    <div className="text-xs text-slate-400">/{blog.slug}</div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600 text-sm whitespace-nowrap">
                                                    {new Date(blog.published_at || blog.created_at).toLocaleDateString('id-ID', {
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric'
                                                    })}
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    {blog.category?.name || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                                        <Eye size={12} /> {blog.view_count || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border ${blog.status === 'published'
                                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                        : 'bg-amber-50 text-amber-600 border-amber-100'
                                                        }`}>
                                                        {blog.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => navigate(`/dashboardadmin/blog/edit/${blog.id}`)}
                                                            className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded"
                                                            title="Edit"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(blog.id)}
                                                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                                                            title="Hapus"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* CATEGORY MODAL */}
                    {showCategoryModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in-up">
                                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                                    <h3 className="font-bold text-slate-800">Kelola Kategori</h3>
                                    <button onClick={() => setShowCategoryModal(false)} className="text-slate-400 hover:text-slate-600">
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="p-6">
                                    <div className="flex gap-2 mb-6">
                                        <input
                                            type="text"
                                            placeholder="Nama Kategori Baru..."
                                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-100 focus:border-sky-500 outline-none"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                        />
                                        <button
                                            onClick={handleAddCategory}
                                            disabled={categoryLoading}
                                            className="bg-sky-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
                                        >
                                            {categoryLoading ? '...' : 'Tambah'}
                                        </button>
                                    </div>

                                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                        {categories.length === 0 && (
                                            <p className="text-center text-sm text-slate-400">Belum ada kategori.</p>
                                        )}
                                        {categories.map(cat => (
                                            <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 group">
                                                <span className="text-sm font-medium text-slate-700">{cat.name}</span>
                                                <button
                                                    onClick={() => handleDeleteCategory(cat.id)}
                                                    className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default AdminBlogPage;
